import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { isNullOrUndefined } from '@app/shared/helpers';
import { environment } from '@environments/environment';
import { PhotoMetaData } from '@models/photo/photo-meta-data';
import * as heicConvert from 'heic-convert/browser';
import * as moment from 'moment';
import { combineLatest, from, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { generateRandomString, removeURIQueryString } from '../lib/fun';
import { BlobService } from './blob.service';
import { ClinicsService } from './clinics.service';

@Injectable()
export class ImageService {
  private photosTabLoadingState = new Subject<any>();
  photosTabLoadingState$ = this.photosTabLoadingState.asObservable();
  private metadataUpdated = new Subject<any>();
  metadataUpdated$ = this.metadataUpdated.asObservable();

  constructor(private http: HttpClient, private blobService: BlobService, private clinicsService: ClinicsService) {}

  photosTabLoadingStateChanged(value) {
    this.photosTabLoadingState.next(value);
  }

  revertPatientPhoto(photoMetaData: PhotoMetaData): Observable<PhotoMetaData> {
    return this.blobService
      .downloadFileFromBlobStorage(photoMetaData.filePathThumb.replace('thumbs', 'originals'))
      .pipe(
        switchMap((downloadResponse) => from(downloadResponse.blobBody)),
        switchMap((blob: Blob) => {
          const arrayOfBlob = new Array<Blob>();
          arrayOfBlob.push(blob);
          const revertedFile = new File(arrayOfBlob, photoMetaData.fileId);

          // due to reverting to original we set the "revertToOriginal" flag to true
          return this.uploadPhoto(revertedFile, photoMetaData, { revertToOriginal: true, createOriginal: false });
        })
      );
  }

  deletePhoto(photoMetaData: PhotoMetaData): Observable<any> {
    return this.http.delete<PhotoMetaData>(`${environment.baseUrl}api/Photo/DeletePhoto?id=${photoMetaData.id}`).pipe(
      map((data) => {
        // this.patientService.thePatientHasBeenUpdatedById(photoMetaData.patientId);
        return data;
      })
    );
  }

  getClinicStockPhotos(): Observable<PhotoMetaData[]> {
    return this.http.get<PhotoMetaData[]>(`${environment.baseUrl}api/Photo/ClinicStockPhotos`).pipe(
      map((photos) => {
        const readOnlySAS = this.blobService.getReadOnlySAS();
        photos.forEach((photo) => {
          photo.filePath = (photo.filePath ? photo.filePath.trim() : '') + readOnlySAS;
          photo.filePathThumb = (photo.filePathThumb ? photo.filePathThumb.trim() : '') + readOnlySAS;
          photo.filePathOriginal = (photo.filePathOriginal ? photo.filePathOriginal.trim() : '') + readOnlySAS;
        });
        return photos;
      })
    );
  }

  downloadImageLine(imageName: string, dataUri: string) {
    var a = document.createElement('a');
    a.download = imageName;
    a.href = dataUri;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  downloadSingleImage(image: PhotoMetaData) {
    return this.http.get(`${environment.baseUrl}api/Photo/DownloadSingleImage?id=${image.id}`, {
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  async uploadPhoto(
    file: File,
    photoMetaData: PhotoMetaData,
    options: { createOriginal?: boolean; revertToOriginal?: boolean } = {}
  ): Promise<PhotoMetaData> {
    const { createOriginal, revertToOriginal } = Object.assign(
      { createOriginal: true, revertToOriginal: false },
      options
    );

    if (!file || file.size === 0) {
      throw new Error('File is empty');
    }

    let fileExtensionPostfix = file.name.split('.').pop();
    const fileSignature = await this.getFileSignature(file);

    // Chrome does not support heic. This results in an empty or incorrect mime type.
    if ((!file.type && fileExtensionPostfix === 'heic') || fileSignature === 'heic') {
      file = new File([file], file.name, { type: 'image/heic' });
      photoMetaData.imageType = 'image/heic';
    }

    if (file.type === 'image/heic') {
      var buffer = await file.arrayBuffer();
      const unitArray = new Uint8Array(buffer);
      const convertedBuffer = await heicConvert({ buffer: unitArray, format: 'JPEG' });
      file = new File([convertedBuffer], file.name.replace(fileExtensionPostfix, 'jpg'), { type: 'image/jpeg' });
      fileExtensionPostfix = 'jpg';
    }

    // if we havn't got a fileName already create a random one
    photoMetaData.fileId = photoMetaData.fileId ? photoMetaData.fileId : `${uuidv4()}.${fileExtensionPostfix}`;
    const newFormData = new FormData();
    newFormData.append(file.name, file);

    var clinicUrl = this.clinicsService.clinicUrl;
    const baseFilePath = photoMetaData.patientId
      ? clinicUrl + `patients/photos/${photoMetaData.patientContainerFolderName}`
      : clinicUrl + 'clinic/photos';

    // Create Thumbnail
    const thumbnail = await this.http
      .post<any>(`${environment.baseUrl}api/Photo/GetThumbnail`, newFormData)
      .toPromise();
    if (!thumbnail) throw new Error('Error creating thumbnail');

    // Convert response to file
    const binaryString = window.atob(thumbnail.fileContents);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const arrayOfBlob = new Array<Blob>();
    arrayOfBlob.push(new Blob([bytes], { type: thumbnail.contentType }));
    const thumbFile = new File(arrayOfBlob, photoMetaData.fileId, { type: thumbnail.contentType });

    // Upload to Azure Blob Storage
    const thumbBlobUri = await this.blobService
      .uploadFileToBlobStorage(thumbFile, `${baseFilePath}/thumbs/${photoMetaData.fileId}`)
      .toPromise();
    photoMetaData.filePathThumb = thumbBlobUri;

    const mainBlobUri = await this.blobService
      .uploadFileToBlobStorage(file, `${baseFilePath}/emily/${photoMetaData.fileId}`)
      .toPromise();
    photoMetaData.filePath = mainBlobUri;

    if (createOriginal) {
      const originalBlobUri = await this.blobService
        .uploadFileToBlobStorage(file, `${baseFilePath}/originals/${photoMetaData.fileId}`)
        .toPromise();
      photoMetaData.filePathOriginal = originalBlobUri;
    }

    // Update Photo Metadata
    let metaData = JSON.parse(JSON.stringify(photoMetaData));
    this.sanitizePhotoMetaData(metaData);
    metaData.isOriginal = revertToOriginal || createOriginal;
    const photoMeta = await this.http
      .put<PhotoMetaData>(`${environment.baseUrl}api/Photo/UpdatePhotoMetaData`, metaData)
      .toPromise();

    // Append SAS
    const readOnlySAS = this.blobService.getReadOnlySAS();
    const forceImageRefreshParam = '&forceRefresh=' + generateRandomString();
    photoMeta.filePath = (photoMeta.filePath ? photoMeta.filePath.trim() : '') + readOnlySAS + forceImageRefreshParam;
    photoMeta.filePathThumb =
      (photoMeta.filePathThumb ? photoMeta.filePathThumb.trim() : '') + readOnlySAS + forceImageRefreshParam;
    photoMeta.filePathOriginal =
      (photoMeta.filePathOriginal ? photoMeta.filePathOriginal.trim() : '') + readOnlySAS + forceImageRefreshParam;
    if (!isNullOrUndefined(photoMeta.seriesPhotos)) {
      photoMeta.seriesPhotos.forEach((sp: PhotoMetaData) => {
        sp.filePath = (sp.filePath ? sp.filePath.trim() : '') + readOnlySAS;
        sp.filePathThumb = (sp.filePathThumb ? sp.filePathThumb.trim() : '') + readOnlySAS;
      });
    }
    return photoMeta;
  }

  async getFileSignature(file: File) {
    // [offset, signature...]
    // https://www.garykessler.net/library/file_sigs.html
    // https://en.wikipedia.org/wiki/List_of_file_signatures
    const signatures = {
      jpeg: [0, 0xff, 0xd8, 0xff],
      png: [0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
      webp: [0, 0x52, 0x49, 0x46, 0x46, 0x57, 0x45, 0x42, 0x50],
      heic: [4, 0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x63],
    };

    const array = await file.arrayBuffer();
    const unit8Array = new Uint8Array(array);
    for (const signature in signatures) {
      if (this.isSignatureMatch(unit8Array, signatures[signature])) {
        return signature;
      }
    }
    return null;
  }

  private isSignatureMatch(unitArray: Uint8Array, signature: number[]): boolean {
    const offset = signature[0];
    const length = signature.length - 1;
    for (let i = 0; i < length; i++) {
      if (unitArray[i + offset] !== signature[i + 1]) {
        return false;
      }
    }
    return true;
  }

  uploadPhotoMetaData(photoMetaData: PhotoMetaData) {
    let series = JSON.parse(JSON.stringify(photoMetaData));
    this.sanitizePhotoMetaData(series);
    // To be called when changes are made to the photo metadata only
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(series));

    return combineLatest([
      this.blobService.getReadOnlySASObservable(),
      this.http.post<PhotoMetaData>(environment.baseUrl + 'api/Photo/MetaData', formData),
    ]).pipe(
      take(1),
      map(([readOnlySAS, photoMeta]) => {
        let photo = new PhotoMetaData(photoMeta, photoMetaData);
        photo.filePath = (photoMeta.filePath ? photoMeta.filePath.trim() : '') + readOnlySAS;
        photo.filePathThumb = (photoMeta.filePathThumb ? photoMeta.filePathThumb.trim() : '') + readOnlySAS;
        photo.filePathOriginal = (photoMeta.filePathOriginal ? photoMeta.filePathOriginal.trim() : '') + readOnlySAS;

        this.metadataUpdated.next(photo);

        return photo;
      })
    );
  }

  uploadUserAvatar(file: File, fileName = ''): Observable<string> {
    // if we havn't got a fileName already create a random one
    fileName = fileName ? fileName : `${uuidv4()}.${file.name.split('.').pop()}`;

    var clinicUrl = this.clinicsService.clinicUrl;
    const filePath = `${clinicUrl}staff/photos/avatar/`;

    return this.blobService.uploadFileToBlobStorage(file, filePath + `${fileName}`, {
      // This options object is purely here to allow you to have a progress function that may update the UI otherwise you can remove it
      progress: (progress) => {
        // TODO: Do something with percent if you want
        const percent = progress.loadedBytes / file.size;
      },
    });
  }

  uploadClinicLogo(file: File, fileName = ''): Observable<string> {
    // if we havn't got a fileName already create a random one
    fileName = fileName ? fileName : `${uuidv4()}.${file.name.split('.').pop()}`;

    var clinicUrl = this.clinicsService.clinicUrl;
    const filePath = `${clinicUrl}clinic/logo/`;

    return this.blobService.uploadFileToBlobStorage(file, filePath + `${fileName}`, {});
  }

  emailPhoto(email: string, photoId: number, filename: string, patientId: number) {
    // let file = new File([blob], filename);
    // const newFormData = new FormData();
    // newFormData.append(file.name, file);

    return this.http.post<any>(
      `${environment.baseUrl}api/Photo/EmailPhoto/${email}/${photoId}/${filename}/${patientId}`,
      {}
    );
  }

  sanitizePhotoMetaData(photoMetaData: PhotoMetaData) {
    if (photoMetaData.filePath) {
      photoMetaData.filePath = removeURIQueryString(photoMetaData.filePath);
    }
    if (photoMetaData.filePathThumb) {
      photoMetaData.filePathThumb = removeURIQueryString(photoMetaData.filePathThumb);
    }
    if (photoMetaData.filePathOriginal) {
      photoMetaData.filePathOriginal = removeURIQueryString(photoMetaData.filePathOriginal);
    }
    if (photoMetaData.seriesPhotos) {
      photoMetaData.seriesPhotos.forEach((sp: PhotoMetaData) => {
        if (sp.filePath) {
          sp.filePath = removeURIQueryString(sp.filePath);
        }
        if (sp.filePathThumb) {
          sp.filePathThumb = removeURIQueryString(sp.filePathThumb);
        }
      });
    }
    if (photoMetaData.uploadDate && photoMetaData.uploadDate.toDate) {
      //@ts-ignore
      photoMetaData.uploadDate = photoMetaData.uploadDate.toDate();
    }
  }

  getConsentedImages() {
    return combineLatest(
      this.blobService.getReadOnlySASObservable(),
      this.http.get<PhotoMetaData[]>(environment.baseUrl + 'api/Photo/Consented')
    ).pipe(
      map(([readOnlySAS, images]) => {
        images.forEach((image) => {
          image.filePath = (image.filePath ? image.filePath.trim() : '') + readOnlySAS;
          image.filePathThumb = (image.filePathThumb ? image.filePathThumb.trim() : '') + readOnlySAS;
          if (!isNullOrUndefined(image.seriesPhotos)) {
            image.seriesPhotos.forEach((sp: PhotoMetaData) => {
              sp.filePath = (sp.filePath ? sp.filePath.trim() : '') + readOnlySAS;
              sp.filePathThumb = (sp.filePathThumb ? sp.filePathThumb.trim() : '') + readOnlySAS;
            });
          }
        });
        return images;
      })
    );
  }

  globalImages: PhotoMetaData[] = null; //caching for the session is likely fine for now -  even faster by including the sas in this variable but then youl be subject to sas expiration too
  getGlobalSharedImages(): Observable<PhotoMetaData[]> {
    return this.globalImages && this.globalImages.length > 0
      ? of(this.globalImages)
      : combineLatest([
          this.blobService.getReadOnlySASObservable(),
          this.http.get<PhotoMetaData[]>(environment.baseUrl + 'api/Photo/GlobalSharedImages'),
        ]).pipe(
          map(([readOnlySAS, images]) => {
            images.forEach((image) => {
              image.filePath = (image.filePath ? image.filePath.trim() : '') + readOnlySAS;
              image.filePathThumb = (image.filePathThumb ? image.filePathThumb.trim() : '') + readOnlySAS;
              if (!isNullOrUndefined(image.seriesPhotos)) {
                image.seriesPhotos.forEach((sp: PhotoMetaData) => {
                  sp.filePath = (sp.filePath ? sp.filePath.trim() : '') + readOnlySAS;
                  sp.filePathThumb = (sp.filePathThumb ? sp.filePathThumb.trim() : '') + readOnlySAS;
                });
              }
            });
            this.globalImages = images;
            return images;
          })
        );
  }

  sortImages(galleryImagesMetaData: PhotoMetaData[]) {
    return galleryImagesMetaData.sort((a, b) => moment.utc(b.uploadDate).diff(moment.utc(a.uploadDate)));
  }

  setPhotoFavouriteStatus(photo: PhotoMetaData, isFavourite: boolean) {
    return this.http
      .put<PhotoMetaData>(environment.baseUrl + 'api/Photo/Favourite/' + photo.id + '/' + isFavourite, {})
      .map((data) => {
        this.metadataUpdated.next(photo);
        return data;
      });
  }
}
